import { useEffect, useMemo, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { FAQS as GENERAL_FAQS } from "@/lib/data";
import SEO from "@/components/SEO";
import { getFAQPageSchema } from "@/lib/seoSchemas";

export default function FaqPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .listServices()
      .then(({ services }) => setServices(services))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const byCategory = new Map();
    for (const s of services) {
      const faqs = s.sections?.faqs;
      if (!Array.isArray(faqs) || !faqs.length) continue;
      const list = byCategory.get(s.category) || [];
      faqs.forEach((f) => list.push({ ...f, serviceTitle: s.title }));
      byCategory.set(s.category, list);
    }
    return Array.from(byCategory.entries()).map(([category, items]) => ({ category, items }));
  }, [services]);

  const allFaqs = useMemo(() => {
    const list = [...GENERAL_FAQS];
    for (const g of groups) {
      list.push(...g.items);
    }
    return list;
  }, [groups]);

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map((g) => ({ ...g, items: g.items.filter((i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)) }))
        .filter((g) => g.items.length)
    : groups;

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions | Biz360 Compliance"
        description="Find clear answers to common questions about company registration, ROC due dates, GST thresholds, Maharashtra Gumasta Shop Act, and online compliance."
        canonical="/faq"
        keywords="compliance FAQ, company registration questions, GST due date FAQ, ROC filing queries, Maharashtra Shop Act FAQ"
        breadcrumbs={[{ label: "FAQ", href: "/faq" }]}
        schema={getFAQPageSchema(allFaqs.length ? allFaqs : GENERAL_FAQS)}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
            <Reveal>
              <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                FAQ
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">
                Every question, answered in one place
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-base text-slate-600 md:text-lg">
                General questions plus FAQs for every service we offer — registration, taxation, compliance, accounting and more.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs..."
                data-testid="faq-search"
                className="mt-6 w-full rounded-full border border-navy-900/15 bg-white px-5 py-3 text-sm text-navy-900 shadow-[0_4px_16px_rgba(0,0,0,0.04)] outline-none focus:border-gold-400"
              />
            </Reveal>
          </div>
        </section>

        <div className="relative mx-auto max-w-4xl px-5 py-16 lg:px-8 md:py-20">
          <div className="space-y-14">
            {!q && (
              <Reveal>
                <h2 className="text-xl font-semibold tracking-tight text-navy-900 md:text-2xl">General Questions</h2>
                <Accordion type="single" collapsible className="mt-5 divide-y divide-navy-900/10" data-testid="faq-general-accordion">
                  {GENERAL_FAQS.map((item, i) => (
                    <AccordionItem key={item.q} value={`general-${i}`} className="border-b border-navy-900/10 py-2">
                      <AccordionTrigger className="text-left text-base font-semibold text-navy-900 hover:no-underline md:text-lg">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            {loading ? (
              <p className="text-center text-slate-500">Loading FAQs...</p>
            ) : filteredGroups.length === 0 ? (
              q && <p className="text-center text-slate-500">No FAQs match "{query}".</p>
            ) : (
              filteredGroups.map((group) => (
                <Reveal key={group.category}>
                  <h2 className="text-xl font-semibold tracking-tight text-navy-900 md:text-2xl">{group.category}</h2>
                  <Accordion type="single" collapsible className="mt-5 divide-y divide-navy-900/10" data-testid={`faq-group-${group.category}`}>
                    {group.items.map((item, i) => (
                      <AccordionItem key={`${group.category}-${i}`} value={`${group.category}-${i}`} className="border-b border-navy-900/10 py-2">
                        <AccordionTrigger className="text-left text-base font-semibold text-navy-900 hover:no-underline md:text-lg">{item.q}</AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-slate-600 md:text-base">
                          {item.a}
                          <span className="mt-2 block text-xs font-medium text-slate-400">— {item.serviceTitle}</span>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
